import type { RendererProps } from "../core/element-registry"
import { useAtomDefaults } from "../contexts/atom-defaults-context"
import { Separator } from "../components/ui/separator"

export function SeparatorRenderer({ element }: RendererProps) {
  // Theme-v2 atom defaults feed the fallback chain:
  //   element.variant/size → atomDefaults.default* → shadcn "default"
  // Note: separator.variant is overloaded as an orientation carrier
  // ("vertical"/"horizontal"); the existing `direction ?? variant` logic
  // below is preserved. The data-* surface exposes the raw variant plus
  // the atom-default fallback for external assertion.
  const atomDefaults = useAtomDefaults("separator")
  const resolvedVariant = element.variant ?? atomDefaults.defaultVariant ?? "default"
  const resolvedSize = (element as { size?: string }).size
    ?? atomDefaults.defaultSize
    ?? "default"

  // Support both element.variant and element.direction for orientation
  const direction = element.orientation ?? element.direction ?? element.variant
  const orientation = direction === "vertical" ? "vertical" : "horizontal"

  // Apply height/width from styling for explicit sizing
  const styling = element.styling as Record<string, unknown> | undefined
  const inlineStyle: React.CSSProperties = {}
  if (styling?.height) inlineStyle.height = `${styling.height}px`
  if (styling?.width) inlineStyle.width = `${styling.width}px`
  const hasStyle = Object.keys(inlineStyle).length > 0

  return (
    <Separator
      orientation={orientation}
      className={element.className}
      style={hasStyle ? inlineStyle : undefined}
      data-testid={`separator-${element.id}`}
      data-atom-type="separator"
      data-variant={resolvedVariant}
      data-size={resolvedSize}
    />
  )
}
